import axios from 'axios'
import { useSnackbar } from 'notistack'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import keycloak from '../keycloak'

export type CustomError = {
  id: string
  message: string
  code: number
  traceId: string
  svcName: string
}

export interface useAPIFetchProps {
  // A callback being invoking after api fetch
  callback: (cancelToken: any) => Promise<any>

  // error will be prompted with use of useSnackbar by default, setting hideError = true to disable showing the error
  hideError?: Boolean
}

export default function useAPIFetch(): {
  // trigger a api fetch request by setting the setRequest prop
  setRequest: Dispatch<SetStateAction<useAPIFetchProps | undefined>>

  // loading indicator when performing api fetch
  isLoading: boolean

  // error occur during api fetch
  error: Error | null
} {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [request, setRequest] = useState<useAPIFetchProps | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!request) return

    const CancelToken = axios.CancelToken
    const source = CancelToken.source()
    const fetchData = async () => {
      setIsLoading(true)

      await request
        .callback(source.token)
        .catch((ex) => {
          console.error("[ERROR when call API] error ex", ex)
          let errMsg: any
          if (axios.isCancel(ex)) {
            return
          } else if (axios.isAxiosError(ex)) {
            if (ex.response) {
            	errMsg = ex.response.data
            } else {
                errMsg = t('Unknown Error')
            }
          } else {
            errMsg = ex.detail
          }

          setError(ex)

          enqueueSnackbar(errMsg ?? "Error in connecting with server", {
            style: { zIndex: 10 },
            variant: 'error',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    fetchData()

    return () => {
      // Cancel request when unmounted
      source.cancel()
    }
  }, [request])

  return { setRequest, isLoading, error }
}
